import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { logout } from "../redux/features/authSlice";
import logo from "../assets/images/ptcgph.png";
import api from "../api/config";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    api
      .post("/users/logout/", {
        refresh_token: localStorage.getItem("refresh_token"),
      })
      .then((response) => {
        if (response.status === 200) {
          dispatch(logout());
          navigate("/login");
        }
      });
  };

  const getUserDisplay = () => {
    if (!user) return "";
    return user.name || user.username || user.email || "";
  };

  const AuthButtons = () => {
    if (isAuthenticated) {
      return (
        <div className="relative">
          <button
            className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center gap-1 focus:outline-none"
            onClick={() => setDropdownOpen((open) => !open)}
            onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
            tabIndex={0}
          >
            Welcome, {getUserDisplay()}
            <svg
              className="w-4 h-4 ml-1"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-gray-800 border border-gray-700 rounded-md shadow-lg z-50">
              <Link
                to={`/profile/${user?.username || user?.id || ""}`}
                className="flex items-center px-4 py-2 text-gray-200 hover:bg-gray-700 transition-colors"
                onClick={() => setDropdownOpen(false)}
              >
                <UserIcon className="w-5 h-5 mr-2" />
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-gray-200 hover:bg-gray-700 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5 mr-2" />
                Logout
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <>
        <Link
          to="/login"
          className="text-gray-300 hover:text-white transition-colors duration-200"
        >
          Login
        </Link>
        <Link
          to="/register"
          className="relative inline-flex items-center px-4 py-2 rounded-lg overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-90 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="absolute inset-0.5 rounded-lg bg-black/90 group-hover:bg-black/80 transition-colors duration-300"></div>
          <span className="relative text-white font-medium">Register</span>
        </Link>
      </>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="text-2xl font-pokemon tracking-wider">
              {/* <span className="text-[#FF5D5D]">POKEMON</span>{' '}
              <span className="text-[#4ADE80]">TCG</span>{' '}
              <span className="text-[#60A5FA]">HUB</span> */}
              <img src={logo} alt="Pokémon TCG Hub" className="w-[140px]" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            <Link
              to="/"
              className="text-gray-300 hover:text-white transition-colors duration-200"
            >
              Home
            </Link>
            <Link
              to="/decks"
              className="text-gray-300 hover:text-white transition-colors duration-200"
            >
              Decks
            </Link>
            <Link
              to="/blog"
              className="text-gray-300 hover:text-white transition-colors duration-200"
            >
              Blog
            </Link>
            <Link
              to="/cards"
              className="text-gray-300 hover:text-white transition-colors duration-200"
            >
              Cards
            </Link>
            <AuthButtons />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white focus:outline-none"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-black/90 border-t border-white/5">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/"
              className="block px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
            >
              Home
            </Link>
            <Link
              to="/decks"
              className="block px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
            >
              Decks
            </Link>
            <Link
              to="/blog"
              className="block px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
            >
              Blog
            </Link>
            {isAuthenticated && (
              <Link
                to="/cards"
                className="block px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
              >
                Cards
              </Link>
            )}
            {isAuthenticated ? (
              <>
                <div className="px-3 py-2 text-gray-300">
                  Welcome, {getUserDisplay()}
                </div>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block px-3 py-2 mt-2 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-90 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                  <div className="absolute inset-0.5 rounded-lg bg-black/90 group-hover:bg-black/80 transition-colors duration-300"></div>
                  <span className="relative text-white font-medium block text-center py-2">
                    Register
                  </span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
